import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { BarChart3, TrendingUp, Users, Activity, Calendar, Loader2, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminReportsTab() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [teamData, setTeamData] = useState({ members: [], tasks: [], requests: [], moods: [], standups: [] });
  const token = localStorage.getItem('token');
  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [memRes, taskRes, reqRes, moodRes, standRes] = await Promise.all([
        axios.get(`${API}/team-members`, h).catch(() => ({ data: [] })),
        axios.get(`${API}/tasks`, h).catch(() => ({ data: [] })),
        axios.get(`${API}/creative-requests`, h).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/mood-analytics`, h).catch(() => ({ data: {} })),
        axios.get(`${API}/standups`, h).catch(() => ({ data: [] })),
      ]);
      setTeamData({
        members: memRes.data.members || memRes.data || [],
        tasks: Array.isArray(taskRes.data) ? taskRes.data : taskRes.data.tasks || [],
        requests: Array.isArray(reqRes.data) ? reqRes.data : reqRes.data.requests || [],
        moods: moodRes.data || {},
        standups: Array.isArray(standRes.data) ? standRes.data : standRes.data.standups || [],
      });
    } catch { toast.error('Failed to load report data'); }
    finally { setLoading(false); }
  };

  const filterByPeriod = (items, dateField = 'created_at') => {
    const now = new Date();
    const cutoff = period === 'today' ? new Date(now.setHours(0, 0, 0, 0)) :
      period === 'week' ? new Date(Date.now() - 7 * 86400000) :
      period === 'month' ? new Date(Date.now() - 30 * 86400000) : new Date(0);
    return items.filter(i => new Date(i[dateField]) >= cutoff);
  };

  const { members, tasks, requests, standups } = teamData;
  const filteredTasks = filterByPeriod(tasks);
  const filteredRequests = filterByPeriod(requests);
  const filteredStandups = filterByPeriod(standups);

  const completedTasks = filteredTasks.filter(t => t.status === 'completed' || t.status === 'done');
  const completedRequests = filteredRequests.filter(r => r.status === 'completed');
  const taskCompletionRate = filteredTasks.length > 0 ? Math.round((completedTasks.length / filteredTasks.length) * 100) : 0;
  const requestCompletionRate = filteredRequests.length > 0 ? Math.round((completedRequests.length / filteredRequests.length) * 100) : 0;

  const memberActivity = members.map(m => {
    const userTasks = filteredTasks.filter(t => t.user_id === m.id);
    const userRequests = filteredRequests.filter(r => r.user_id === m.id || r.assigned_to === m.id);
    const userStandups = filteredStandups.filter(s => s.user_id === m.id);
    return { ...m, taskCount: userTasks.length, completedTasks: userTasks.filter(t => t.status === 'completed' || t.status === 'done').length, requestCount: userRequests.length, standupCount: userStandups.length, score: userTasks.length * 2 + userStandups.length + userRequests.length };
  }).sort((a, b) => b.score - a.score);

  const requestTypes = filteredRequests.reduce((acc, r) => { acc[r.request_type || 'other'] = (acc[r.request_type || 'other'] || 0) + 1; return acc; }, {});
  const typeLabels = { poster: 'Poster', banner: 'Banner', video: 'Video', social_media: 'Social Media', other: 'Other' };
  const typeColors = { poster: '#EC4899', banner: '#3B82F6', video: '#8B5CF6', social_media: '#06B6D4', other: '#6B7280' };

  const dailyTrend = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date(Date.now() - i * 86400000);
    const dayStr = date.toDateString();
    const dayTasks = tasks.filter(t => new Date(t.created_at).toDateString() === dayStr);
    const dayRequests = requests.filter(r => new Date(r.created_at).toDateString() === dayStr);
    const dayStandups = standups.filter(s => new Date(s.created_at).toDateString() === dayStr);
    dailyTrend.push({ day: date.toLocaleDateString('en-MY', { weekday: 'short', day: 'numeric' }), tasks: dayTasks.length, requests: dayRequests.length, standups: dayStandups.length, total: dayTasks.length + dayRequests.length + dayStandups.length });
  }
  const maxDaily = Math.max(...dailyTrend.map(d => d.total), 1);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-reports-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E63946] to-[#DC2626] flex items-center justify-center shadow-lg shadow-red-500/30">
            <BarChart3 size={24} className="text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Reports & Analytics</h2>
            <p className="text-sm text-[hsl(var(--text-muted))]">Team performance insights and activity metrics</p>
          </div>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-36 h-9 bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid="reports-period-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3"><Activity size={18} className="text-blue-400" /><Badge className="bg-blue-500/10 text-blue-400 text-xs">{period}</Badge></div>
          <p className="text-3xl font-bold">{filteredTasks.length}</p>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-1">Tasks Created</p>
          <div className="flex items-center gap-1 mt-2 text-xs"><span className={`flex items-center ${taskCompletionRate >= 50 ? 'text-emerald-400' : 'text-amber-400'}`}>{taskCompletionRate >= 50 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}{taskCompletionRate}%</span><span className="text-[hsl(var(--text-muted))]">completion</span></div>
        </div>
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3"><TrendingUp size={18} className="text-pink-400" /><Badge className="bg-pink-500/10 text-pink-400 text-xs">{completedRequests.length} done</Badge></div>
          <p className="text-3xl font-bold">{filteredRequests.length}</p>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-1">Creative Requests</p>
          <div className="flex items-center gap-1 mt-2 text-xs"><span className={`flex items-center ${requestCompletionRate >= 50 ? 'text-emerald-400' : 'text-amber-400'}`}>{requestCompletionRate >= 50 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}{requestCompletionRate}%</span><span className="text-[hsl(var(--text-muted))]">completion</span></div>
        </div>
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3"><Users size={18} className="text-emerald-400" /></div>
          <p className="text-3xl font-bold">{filteredStandups.length}</p>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-1">Standup Entries</p>
        </div>
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3"><Calendar size={18} className="text-amber-400" /></div>
          <p className="text-3xl font-bold">{members.length}</p>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-1">Team Members</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Activity Trend */}
        <div className="lg:col-span-2 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><BarChart3 size={16} className="text-[#E63946]" /> Daily Activity (Last 7 Days)</h3>
          <div className="flex items-end gap-2 h-32">
            {dailyTrend.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex flex-col gap-0.5" style={{ height: '100px' }}>
                  <div className="w-full bg-blue-500/80 rounded-t" style={{ height: `${(d.tasks / maxDaily) * 100}px`, minHeight: d.tasks > 0 ? '3px' : '0' }} title={`${d.tasks} tasks`} />
                  <div className="w-full bg-pink-500/80" style={{ height: `${(d.requests / maxDaily) * 100}px`, minHeight: d.requests > 0 ? '3px' : '0' }} title={`${d.requests} requests`} />
                  <div className="w-full bg-emerald-500/80 rounded-b" style={{ height: `${(d.standups / maxDaily) * 100}px`, minHeight: d.standups > 0 ? '3px' : '0' }} title={`${d.standups} standups`} />
                </div>
                <span className="text-[9px] text-[hsl(var(--text-muted))]">{d.day}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-3 text-[10px] text-[hsl(var(--text-muted))]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 rounded-sm" />Tasks</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-pink-500 rounded-sm" />Requests</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm" />Standups</span>
          </div>
        </div>

        {/* Request Type Distribution */}
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
          <h3 className="text-sm font-semibold mb-4">Request Types</h3>
          <div className="space-y-3">
            {Object.entries(requestTypes).sort((a, b) => b[1] - a[1]).map(([type, count]) => {
              const maxCount = Math.max(...Object.values(requestTypes));
              return (
                <div key={type}>
                  <div className="flex justify-between text-xs mb-1"><span>{typeLabels[type] || type}</span><span className="font-mono">{count}</span></div>
                  <div className="w-full h-2 bg-[hsl(var(--background))] rounded-full"><div className="h-full rounded-full transition-all" style={{ width: `${(count / maxCount) * 100}%`, backgroundColor: typeColors[type] || '#6B7280' }} /></div>
                </div>
              );
            })}
            {Object.keys(requestTypes).length === 0 && <p className="text-sm text-[hsl(var(--text-muted))] text-center py-4">No requests in this period</p>}
          </div>
        </div>
      </div>

      {/* Team Member Leaderboard */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><Users size={16} className="text-emerald-400" /> Team Activity Leaderboard</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[hsl(var(--border))]">
                <th className="text-left py-3 px-3 text-[hsl(var(--text-muted))] font-medium text-xs">#</th>
                <th className="text-left py-3 px-3 text-[hsl(var(--text-muted))] font-medium text-xs">Member</th>
                <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-medium text-xs">Tasks</th>
                <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-medium text-xs">Completed</th>
                <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-medium text-xs">Requests</th>
                <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-medium text-xs">Standups</th>
                <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-medium text-xs">Score</th>
              </tr>
            </thead>
            <tbody>
              {memberActivity.slice(0, 15).map((m, i) => (
                <tr key={m.id} className="border-b border-[hsl(var(--border))]/50 hover:bg-[hsl(var(--background))]">
                  <td className="py-3 px-3 text-xs text-[hsl(var(--text-muted))]">{i + 1}</td>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#E63946] to-[#DC2626] flex items-center justify-center text-white text-[10px] font-bold">{m.name?.charAt(0)}</div>
                      <div><p className="text-sm font-medium">{m.name}</p><p className="text-[10px] text-[hsl(var(--text-muted))]">{m.role_id}</p></div>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-center font-mono">{m.taskCount}</td>
                  <td className="py-3 px-3 text-center"><Badge className={`text-[10px] ${m.completedTasks > 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))]'}`}>{m.completedTasks}</Badge></td>
                  <td className="py-3 px-3 text-center font-mono">{m.requestCount}</td>
                  <td className="py-3 px-3 text-center font-mono">{m.standupCount}</td>
                  <td className="py-3 px-3 text-center"><Badge className="bg-[#E63946]/10 text-[#E63946] font-bold">{m.score}</Badge></td>
                </tr>
              ))}
              {memberActivity.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[hsl(var(--text-muted))]">No team activity data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
